import { Injectable } from '@angular/core';
import { Platform } from '@ionic/angular';
import { DocumentPicker } from '@awesome-cordova-plugins/document-picker/ngx';
import { FileOpener } from '@awesome-cordova-plugins/file-opener/ngx';
import {
  FileEntry,
  File as NativeFile,
  IFile,
} from '@awesome-cordova-plugins/file/ngx';
import {
  FileTransfer,
  FileTransferObject,
} from '@awesome-cordova-plugins/file-transfer/ngx';
import { FileChooser } from '@awesome-cordova-plugins/file-chooser/ngx';
import { AttachmentHttpService } from '@app/core/async-services/http/versioned/attachment.http';
import { fileUtils } from '@app/core/utils';
import { FilePath } from '@awesome-cordova-plugins/file-path/ngx';
import { File } from '@app/core/models';
import { FileReaderService } from '@app/core/services/file-reader/file-reader.service';
import { firstValueFrom } from 'rxjs';

export class PlatformError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'PlatformError';
    // ES5 limitation - https://github.com/Microsoft/TypeScript-wiki/blob/master/Breaking-Changes.md#extending-built-ins-like-error-array-and-map-may-no-longer-work
    Object.setPrototypeOf(this, PlatformError.prototype);
  }
}

export class InvalidFileError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'InvalidFileError';
    // ES5 limitation - https://github.com/Microsoft/TypeScript-wiki/blob/master/Breaking-Changes.md#extending-built-ins-like-error-array-and-map-may-no-longer-work
    Object.setPrototypeOf(this, InvalidFileError.prototype);
  }
}

@Injectable({
  providedIn: 'root',
})
export class FileService {
  private _fileTransferObject: FileTransferObject;

  constructor(
    private _platform: Platform,
    private _file: NativeFile,
    private _fileOpener: FileOpener,
    private _fileTransfer: FileTransfer,
    private _iosFilePicker: DocumentPicker,
    private _fileChooser: FileChooser,
    private _attachmentHttpService: AttachmentHttpService,
    private _filePath: FilePath,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    private fileReaderService: FileReaderService,
  ) {
    this._fileTransferObject = this._fileTransfer.create();
  }

  async pick(): Promise<File.Raw> {
    if (!this._platform.is('capacitor')) {
      throw new PlatformError('File picker is not supported on desktop.');
    }
    const filePath = this._platform.is('ios')
      ? await this._iosFilePicker.getFile().then(fileUtils.resolveIOSPath)
      : await this._fileChooser
          .open()
          .then((path) => this._filePath.resolveNativePath(path));
    await this._validateFile(filePath);
    const fileName = fileUtils.getFileName(filePath);
    const blob = await this._readFileFromDevice(filePath);
    return { fileName, blob };
  }

  async upload({ fileName, blob }: File.Raw): Promise<{ url: string }> {
    const response = await firstValueFrom(
      this._attachmentHttpService.getDocumentPutUrl(fileName, blob.type),
    );
    const secureUrl = response.payload!.url;
    await firstValueFrom(
      this._attachmentHttpService.putToAmazon(secureUrl, blob),
    );
    return { url: secureUrl };
  }

  async previewDocument(documentId: string): Promise<void> {
    const url = await this._getDocumentUrl(documentId);
    this.downloadAndPreview(url);
  }

  async downloadAndPreview(url: string): Promise<void> {
    if (this._platform.is('ios')) {
      const fileEntry = await this._download(url);
      this._previewOnIos(fileEntry);
    } else if (this._platform.is('android')) {
      const fileEntry = await this._download(url);
      this._previewOnAndroid(fileEntry, url);
    } else {
      window.open(url, '_blank');
    }
  }

  private async _validateFile(filePath: string): Promise<void> {
    this._validateFileType(filePath);
    await this._validateFileSize(filePath);
  }

  private _validateFileType(filePath: string): void {
    const extension = fileUtils.getExtension(filePath);
    if (!fileUtils.isValidExtension(extension)) {
      const validExtensions = `.${fileUtils.VALID_EXTENSIONS.join(', .')}`;
      throw new InvalidFileError(
        `.${extension} is not a supported file type. Please choose one of the following file types instead: ${validExtensions}`,
      );
    }
  }

  private async _validateFileSize(filePath: string): Promise<void> {
    const entry = await this._file.resolveLocalFilesystemUrl(filePath);
    const size = await new Promise<number>((resolve, reject) =>
      entry.getMetadata(
        (meta) => resolve(meta.size),
        (error) => reject(error),
      ),
    );
    if (size > 50000000) {
      throw new InvalidFileError(
        'File is too large. Please choose a file no larger than 50MB.',
      );
    }
  }

  private async _readFileFromDevice(filePath: string): Promise<Blob> {
    const file = await this._getFile(filePath);
    return this.fileReaderService.readAsArrayBuffer(file);
  }

  private async _getFile(filePath: string): Promise<IFile> {
    const fileEntry = await this._getFileEntry(filePath);
    return new Promise<IFile>((resolve, reject) => {
      fileEntry.file(
        (iFile) => resolve(iFile),
        (error) => reject(error),
      );
    });
  }

  private async _getFileEntry(filePath: string): Promise<FileEntry> {
    const { directoryUrl, fileName } = fileUtils.getFilePathSegments(filePath);
    const directoryEntry = await this._file.resolveDirectoryUrl(directoryUrl);
    return this._file.getFile(directoryEntry, fileName, { create: false });
  }

  private async _getDocumentUrl(documentId: string): Promise<string> {
    const response = await firstValueFrom(
      this._attachmentHttpService.getDocument(documentId),
    );
    return response.payload!.url;
  }

  private _download(url: string): Promise<FileEntry> {
    const target = this._getTarget(url);
    return this._fileTransferObject.download(url, target);
  }

  private async _previewOnIos(fileEntry: FileEntry): Promise<void> {
    const filePath = fileEntry.toURL();
    const mimeType = await this._getMimeType(fileEntry);
    this._fileOpener.open(filePath, mimeType);
  }

  private async _previewOnAndroid(
    fileEntry: FileEntry,
    url: string,
  ): Promise<void> {
    const mimeType = await this._getMimeType(fileEntry);
    const target = this._getTarget(url);
    this._fileOpener.showOpenWithDialog(target, mimeType);
  }

  private _getMimeType(fileEntry: FileEntry): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      fileEntry.file(
        (iFile) => resolve(iFile.type),
        (error) => reject(error),
      );
    });
  }

  private _getTarget(url: string): string {
    const directory = this._platform.is('ios')
      ? this._file.tempDirectory
      : this._file.cacheDirectory;
    const fileName = fileUtils.getFileName(url);
    return directory + fileName;
  }
}
